import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { SampleUser } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import './groupChat.css'

// Wired to GET/POST /splitwise/groups/<group_id>/messages/ + the live
// WebSocket at /ws/splitwise/groups/<group_id>/chat/?token=... . Figma
// showed a couple of things this API doesn't actually back: an "online
// members" presence line and call (phone/video) icons in the header, plus
// system-style "X added ₹Y for..." / "✓ Priya settled ₹X with you" bubbles
// — the real `message_type` enum is only `text` | `reminder`, nothing for
// expense-added/payment-settled activity. None of that is built here;
// only `text` bubbles and `reminder` system-style bubbles are real.
const buildWsUrl = (groupId, token) => {
    try {
        const api = new URL(process.env.REACT_APP_API_BASE_URL || '')
        const proto = api.protocol === 'https:' ? 'wss:' : 'ws:'
        return `${proto}//${api.host}/ws/splitwise/groups/${groupId}/chat/?token=${encodeURIComponent(token || '')}`
    } catch {
        return ''
    }
}

const formatTime = (value) => {
    if (!value) return ''
    const date = new Date(String(value).replace(' ', 'T'))
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })
}

const messageKey = (msg) => msg.id ?? msg.message_id

const AttachIcon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <path d="M13.5 6.5 7.9 12.1a2.5 2.5 0 0 0 3.54 3.54l6.02-6.02a4 4 0 0 0-5.66-5.66L5.78 9.98a5.5 5.5 0 0 0 7.78 7.78" stroke="#6B7280" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const SendIcon = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
        <path d="M2.5 10 17.5 2.5 12.5 17.5 9.5 11.5 2.5 10Z" stroke="#fff" strokeWidth="1.4" strokeLinejoin="round" fill="none" />
    </svg>
)

const GroupChat = ({ groupId }) => {
    const accessToken = useSelector((state) => state.auth.access_token)
    const currentUserId = useSelector((state) => state.auth.user?.id)

    const [messages, setMessages] = useState([])
    const [loading, setLoading] = useState(true)
    const [text, setText] = useState('')
    const [attachment, setAttachment] = useState(null)
    const [sending, setSending] = useState(false)
    const [wsConnected, setWsConnected] = useState(false)

    const wsRef = useRef(null)
    const listEndRef = useRef(null)
    const fileInputRef = useRef(null)

    const addMessage = (msg) => {
        const key = messageKey(msg)
        setMessages((prev) => (prev.some((m) => messageKey(m) === key) ? prev : [...prev, msg]))
    }

    useEffect(() => {
        if (!groupId) return
        setLoading(true)
        service
            .get(endpoints.splitwise.groupMessages.replace('{group_id}', groupId), { page: 1, page_size: 20 }, { silent: true })
            .then(({ data }) => {
                const list = Array.isArray(data.data) ? data.data : []
                // API returns newest-first; chat UI reads oldest-at-top.
                setMessages([...list].reverse())
            })
            .catch(() => setMessages([]))
            .finally(() => setLoading(false))
    }, [groupId])

    useEffect(() => {
        if (!groupId || !accessToken) return
        const url = buildWsUrl(groupId, accessToken)
        if (!url) return
        const ws = new WebSocket(url)
        wsRef.current = ws

        ws.onopen = () => setWsConnected(true)
        ws.onclose = () => setWsConnected(false)
        ws.onerror = () => setWsConnected(false)
        ws.onmessage = (event) => {
            try {
                const payload = JSON.parse(event.data)
                // chat_history is skipped — the GET above already renders the
                // initial page; this only appends genuinely new live messages.
                if (payload.type === 'chat_message') addMessage(payload)
            } catch {
                // ignore malformed frames
            }
        }

        return () => {
            ws.close()
            wsRef.current = null
        }
    }, [groupId, accessToken])

    useEffect(() => {
        listEndRef.current?.scrollIntoView({ block: 'end' })
    }, [messages.length])

    const handleAttachmentChange = (e) => {
        const file = e.target.files?.[0]
        if (file) setAttachment(file)
        e.target.value = ''
    }

    const handleSend = () => {
        const trimmed = text.trim()
        if ((!trimmed && !attachment) || sending) return
        setSending(true)

        if (attachment) {
            // Attachments always go via POST (multipart) — simpler and more
            // robust than base64-encoding a file into a WS frame.
            const formData = new FormData()
            if (trimmed) formData.append('message', trimmed)
            formData.append('attachment', attachment)
            service
                .post(endpoints.splitwise.groupMessages.replace('{group_id}', groupId), formData, { silent: true })
                .then(({ data }) => {
                    if (data?.data) addMessage(data.data)
                    setText('')
                    setAttachment(null)
                })
                .catch(() => {
                    // error toast already shown by the service interceptor
                })
                .finally(() => setSending(false))
            return
        }

        // Text-only: send over the live socket when it's up — the server
        // broadcasts it straight back as a chat_message event, so it lands
        // through the exact same render path as everyone else's messages.
        if (wsConnected && wsRef.current) {
            wsRef.current.send(JSON.stringify({ message: trimmed }))
            setText('')
            setSending(false)
        } else {
            service
                .post(endpoints.splitwise.groupMessages.replace('{group_id}', groupId), { message: trimmed }, { silent: true })
                .then(({ data }) => {
                    if (data?.data) addMessage(data.data)
                    setText('')
                })
                .catch(() => {
                    // error toast already shown by the service interceptor
                })
                .finally(() => setSending(false))
        }
    }

    return (
        <div className='groupChat'>
            <div className='groupChat__messages'>
                {loading ? (
                    <p className='bsFareNote'>Loading messages...</p>
                ) : messages.length ? messages.map((msg) => {
                    const key = messageKey(msg)
                    const isMine = msg.sender_id === currentUserId

                    if (msg.message_type === 'reminder') {
                        return <div className='groupChat__system' key={key}>🔔 {msg.message}</div>
                    }

                    return (
                        <div className={`groupChat__row ${isMine ? 'mine' : ''}`} key={key}>
                            {!isMine && <img src={msg.sender_image || SampleUser} alt="" className='groupChat__avatar' />}
                            <div className='groupChat__bubbleCol'>
                                {!isMine && <div className='groupChat__sender'>{msg.sender_name}</div>}
                                <div className='groupChat__bubble'>
                                    {msg.attachment_url && (
                                        <a href={msg.attachment_url} target='_blank' rel='noreferrer' className='groupChat__attachmentLink'>📎 Attachment</a>
                                    )}
                                    {msg.message && <div>{msg.message}</div>}
                                </div>
                                <div className='groupChat__time'>{formatTime(msg.created_at)}</div>
                            </div>
                        </div>
                    )
                }) : (
                    <p className='bsFareNote'>No messages yet. Say hi!</p>
                )}
                <div ref={listEndRef} />
            </div>

            {attachment && (
                <div className='groupChat__attachmentPreview'>
                    📎 {attachment.name}
                    <button type='button' onClick={() => setAttachment(null)}>×</button>
                </div>
            )}

            <div className='groupChat__composer'>
                <button type='button' className='groupChat__attachBtn' onClick={() => fileInputRef.current?.click()}>
                    <AttachIcon />
                </button>
                <input ref={fileInputRef} type='file' style={{ display: 'none' }} onChange={handleAttachmentChange} />
                <input
                    type='text'
                    placeholder='Type your message here...'
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSend() }}
                />
                <button type='button' className='groupChat__sendBtn' disabled={sending} onClick={handleSend}>
                    <SendIcon />
                </button>
            </div>
        </div>
    )
}

export default GroupChat
